import React, { useRef } from 'react';
import { Box, CircularProgress } from '@/ui';
import Header from '@/components/Header';
import Footer from '@/components/on-boarding/Footer';
import { OuterNavChips, InnerNavDesktop, SectionWarnings } from './';
import type { ResolvedWarning, WarningToken } from '@/shared/warnings/types';

interface Route {
  key: string;
  label: string;
}

interface DesktopLayoutProps {
  outerRoutes: Route[];
  selectedOuter: string;
  currentInnerRoutes: Route[];
  selectedInner: string;
  sectionWarnings: ResolvedWarning[];
  nextButtonLabel: string;
  currentFormId: string;
  nextButtonLoading: boolean;
  nextButtonDisabled: boolean;
  isNavigating: boolean;
  onOuterRouteChange: (routeKey: string) => Promise<void>;
  onNavigateToInner: (key: string) => void;
  onSectionTokenClick: (token: WarningToken) => Promise<void>;
  onBackNav: () => Promise<void>;
  renderBudgetForm: () => React.ReactNode;
  /** When true, transforms "council tax" to "council rates" for NI users */
  isNorthernIreland?: boolean;
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  outerRoutes,
  selectedOuter,
  currentInnerRoutes,
  selectedInner,
  sectionWarnings,
  nextButtonLabel,
  currentFormId,
  nextButtonLoading,
  nextButtonDisabled,
  isNavigating,
  onOuterRouteChange,
  onNavigateToInner,
  onSectionTokenClick,
  onBackNav,
  renderBudgetForm,
  isNorthernIreland = false,
}) => {
  const desktopContentRef = useRef<HTMLDivElement | null>(null);

  return (
    <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{
      height: '100vh',
      width: '100vw',
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      backgroundColor: '#f9fafb',
      overflow: 'hidden'
    }}>
      {/* Header for Desktop */}
      <Header data-testid={`update-budget-${selectedOuter}-${selectedInner}-header-action`} />

      {/* Content Area - Centered Container */}
      <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} ref={desktopContentRef} sx={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f9fafb',
        px: 2,
        py: 2,
        width: '100%',
        minWidth: 0
      }}>
        <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f9fafb',
          minHeight: 'min-content'
        }}>
          {/* Content */}
          <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f9fafb',
            alignItems: 'center',
            width: '100%',
            minHeight: 0
          }}>
            <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ 
              px: 3, 
              py: 2,
              width: '100%',
              maxWidth: '900px'
            }}>
              {/* Top Navigation - Outer Routes */}
              <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ mb: 2 }}>
                <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  mb: 2,
                  // Hide scrollbar while keeping scroll functionality
                  scrollbarWidth: 'none', // Firefox
                  msOverflowStyle: 'none', // IE and Edge
                  '&::-webkit-scrollbar': {
                    display: 'none' // Chrome, Safari, Opera
                  }
                }}>
                  <OuterNavChips data-testid={`update-budget-${selectedOuter}-${selectedInner}-nav-tabs-outer-on-outer-route-change`} 
                    routes={outerRoutes} 
                    selectedKey={selectedOuter} 
                    onSelect={onOuterRouteChange} 
                  />
                </Box>
              </Box>

              {/* Second Navigation - Inner Routes */}
              <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ mb: 3 }}>
                <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <InnerNavDesktop data-testid={`update-budget-${selectedOuter}-${selectedInner}-nav-tabs-inner-desktop-on-navigate-to-inner`}
                    routes={currentInnerRoutes}
                    selectedKey={selectedInner}
                    onSelect={onNavigateToInner}
                  />
                </Box>
              </Box>

              {/* Main Content */}
              <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{ flex: 1, pb: 2 }}>
                {/* Section-level Warning Panel (above form) */}
                <SectionWarnings
                  warnings={sectionWarnings}
                  onTokenClick={onSectionTokenClick}
                  isNorthernIreland={isNorthernIreland}
                />
                {renderBudgetForm()}
              </Box>

              {/* Desktop Footer - Below Content */}
              <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`} sx={{
                mt: 2
              }}>
                <Footer
                  handleBack={onBackNav}
                  shouldApplyTopBorder={false}
                  shouldApplyBackgroundColor={false}
                  shouldBackDisabled={false}
                  nextButtonText={nextButtonLabel}
                  formId={currentFormId}
                  nextButtonLoading={nextButtonLoading}
                  nextButtonDisabled={nextButtonDisabled}
                  showSecondaryButton={false}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading Overlay for Navigation */}
      {isNavigating && (
        <Box data-testid={`update-budget-${selectedOuter}-${selectedInner}-container-action`}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // Use a light overlay so high‑contrast / Recite Me modes
            // don’t appear as a solid black screen when navigating
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={50} sx={{ color: '#000' }} />
        </Box>
      )}
    </Box>
  );
};

export default DesktopLayout;

